import type { DocumentType } from "../types";

/**
 * The document-type tabs shown in the notification worklist, in display order.
 *
 * Single source of truth for "which tabs are visible" — consumed both by the
 * `DocumentTypeTabs` component (to render the strip) and by `NotificationsPage`
 * (to validate the deep-linkable `?tab=` param). Kept here, next to the
 * `DocumentType` it constrains, rather than inside a view component so the
 * dependency arrow points from pages/views down to shared config (no layering
 * inversion, no import cycle).
 *
 * "ECOG" and "Prescriptions" are intentionally omitted (hidden per product
 * requirement); their `DocumentType` members, summary views, and data plumbing
 * remain, so re-adding either here re-enables its tab everywhere.
 */
export const DOCUMENT_TYPE_TABS = [
  "Radiology",
  "Molecular Results",
  "Pathology Results",
  "MD Visit Note",
  "Lab Results",
] as const satisfies readonly DocumentType[];

/**
 * A document type whose tab is currently visible in the worklist.
 *
 * The literal-preserving `as const` above makes this the exact union of visible
 * tabs (excluding the hidden "ECOG" and "Prescriptions"), so what is hidden is
 * enforced at the type level rather than by comment, and `DOCUMENT_TYPE_TABS[0]`
 * is provably `"Radiology"` (non-empty) instead of a by-convention default.
 */
export type VisibleDocumentType = (typeof DOCUMENT_TYPE_TABS)[number];

/**
 * Type guard that narrows an untrusted string to a currently-visible tab.
 *
 * Validates a raw value (e.g. a `?tab=` query param) against
 * {@link DOCUMENT_TYPE_TABS} at runtime while giving the compiler an honest
 * `value is VisibleDocumentType` narrowing — no unchecked `as DocumentType` cast.
 *
 * @param value - Raw candidate tab value, typically from the URL (may be null)
 * @returns True when `value` names a currently-visible document-type tab
 */
export function isVisibleDocumentTypeTab(
  value: string | null,
): value is VisibleDocumentType {
  return (
    value !== null && (DOCUMENT_TYPE_TABS as readonly string[]).includes(value)
  );
}
